import { Controller } from "@hotwired/stimulus"
import { DeviceDataCollector } from "../device_data_collector"
import { showPaymentProcessingOverlay } from "../payment_processing_overlay"

export default class extends Controller {
  static targets = [
    "pixInfo", "creditCardForm", "cardResult", "creditCardDiv",
    "paymentCardCheck", "paymentPixCheck", "pixResult", "pixText",
    "paymentCardDiv", "paymentPixDiv", "endBuyButton", "paymentMethodsDiv",
    "captchaDiv", "form", "sellerCode", "formSection", "paymentPlaceholder"
  ]

  static values = {
    hasErrors: { type: Boolean, default: false }
  }

  connect() {
    // Sync seller code fields when they change (mobile/desktop)
    this.syncSellerCodeFields()

    // Only preserve payment method selection if this is a re-render after validation error
    // This prevents browser form restoration from auto-selecting payment methods on fresh load
    if (this.hasErrorsValue) {
      const creditCardRadio = document.querySelector('input[name="bill_info[payment_method]"][value="credit_card"]')
      const pixRadio = document.querySelector('input[name="bill_info[payment_method]"][value="pix"]')

      if (creditCardRadio?.checked) {
        // Preserve credit card selection and show form
        this.initializePaymentMethod('credit_card')
      } else if (pixRadio?.checked) {
        // Preserve PIX selection and show form
        this.initializePaymentMethod('pix')
      } else {
        this.deselectPaymentMethods()
      }
    } else {
      // Fresh page load - always deselect payment methods
      // (prevents browser form state restoration from selecting a payment method)
      this.deselectPaymentMethods()
    }
  }

  initializePaymentMethod(method) {
    // Show the form sections
    if (this.hasFormSectionTarget) {
      this.formSectionTargets.forEach(section => {
        section.classList.remove("hidden")
      })
    }

    // Hide the placeholder prompt
    if (this.hasPaymentPlaceholderTarget) {
      this.paymentPlaceholderTarget.classList.add("hidden")
    }

    if (method === "pix") {
      this.pixInfoTarget.classList.remove("hidden")
      this.creditCardDivTarget.classList.add("hidden")
      this.paymentCardCheckTarget.classList.add("hidden")
      this.paymentPixCheckTarget.classList.remove("hidden")
      this.paymentCardDivTarget.classList.remove("selected")
      this.paymentPixDivTarget.classList.add("selected")
    } else if (method === "credit_card") {
      this.pixInfoTarget.classList.add("hidden")
      this.creditCardDivTarget.classList.remove("hidden")
      this.paymentCardCheckTarget.classList.remove("hidden")
      this.paymentPixCheckTarget.classList.add("hidden")
      this.paymentCardDivTarget.classList.add("selected")
      this.paymentPixDivTarget.classList.remove("selected")
    }
  }

  deselectPaymentMethods() {
    // Uncheck all payment method radio buttons on page load
    const creditCardRadio = document.querySelector('input[name="bill_info[payment_method]"][value="credit_card"]')
    const pixRadio = document.querySelector('input[name="bill_info[payment_method]"][value="pix"]')

    if (creditCardRadio) creditCardRadio.checked = false
    if (pixRadio) pixRadio.checked = false

    // Remove selected class from both payment option cards
    if (this.hasPaymentCardDivTarget) {
      this.paymentCardDivTarget.classList.remove("selected")
    }
    if (this.hasPaymentPixDivTarget) {
      this.paymentPixDivTarget.classList.remove("selected")
    }

    // Hide the check icons
    if (this.hasPaymentCardCheckTarget) {
      this.paymentCardCheckTarget.classList.add("hidden")
    }
    if (this.hasPaymentPixCheckTarget) {
      this.paymentPixCheckTarget.classList.add("hidden")
    }
  }

  // Synchronize seller code fields between mobile and desktop views
  syncSellerCodeFields() {
    const sellerCodeFields = document.querySelectorAll('[name="seller_info[code]"]')
    sellerCodeFields.forEach(field => {
      field.addEventListener('input', (e) => {
        const newValue = e.target.value
        sellerCodeFields.forEach(otherField => {
          if (otherField !== e.target) {
            otherField.value = newValue
          }
        })
      })
    })
  }

  selectPaymentMethod(event) {
    const selectedMethod = event.target.value

    // Show the form sections
    if (this.hasFormSectionTarget) {
      this.formSectionTargets.forEach(section => {
        section.classList.remove("hidden")
      })
    }

    // Hide the placeholder prompt
    if (this.hasPaymentPlaceholderTarget) {
      this.paymentPlaceholderTarget.classList.add("hidden")
    }

    if (selectedMethod === "pix") {
      this.pixInfoTarget.classList.remove("hidden")
      this.creditCardDivTarget.classList.add("hidden")
      this.paymentCardCheckTarget.classList.add("hidden")
      this.paymentPixCheckTarget.classList.remove("hidden")
      this.paymentCardDivTarget.classList.remove("selected")
      this.paymentPixDivTarget.classList.add("selected")

      // Scroll to PIX info section (centered)
      setTimeout(() => {
        this.pixInfoTarget.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }, 100)
    } else if (selectedMethod === "credit_card") {
      this.pixInfoTarget.classList.add("hidden")
      this.creditCardDivTarget.classList.remove("hidden")
      this.paymentCardCheckTarget.classList.remove("hidden")
      this.paymentPixCheckTarget.classList.add("hidden")
      this.paymentCardDivTarget.classList.add("selected")
      this.paymentPixDivTarget.classList.remove("selected")

      // Scroll to credit card form (centered)
      setTimeout(() => {
        this.creditCardDivTarget.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }, 100)
    }
  }

  submitPayment(event) {
    event.preventDefault()

    const form = event.target

    // Collect device data before creating FormData
    const deviceData = DeviceDataCollector.collect()
    const deviceDataField = form.querySelector('input[name="device_data"]')
    if (deviceDataField) {
      deviceDataField.value = JSON.stringify(deviceData)
    }

    const formData = new FormData(form)

    // Check if split payment overlay already exists (created by split-payment controller)
    const existingSplitOverlay = document.getElementById('split-payment-processing-overlay')
    let overlay = existingSplitOverlay

    if (!existingSplitOverlay) {
      // Only create overlay if one doesn't exist
      document.body.style.overflow = 'hidden'

      overlay = document.createElement('div')
      overlay.id = 'payment-processing-overlay'
      overlay.className = 'fixed inset-0 bg-white z-50 flex items-center justify-center'
      overlay.innerHTML = `
        <div class="text-center space-y-6">
          <!-- Loading Spinner -->
          <div class="flex justify-center">
            <div class="w-16 h-16 border-4 border-gray-200 border-t-green-600 rounded-full animate-spin"></div>
          </div>

          <!-- Loading Text -->
          <div class="space-y-2">
            <h3 class="text-xl font-semibold text-gray-800">
              Processando pagamento...
            </h3>
            <p class="text-gray-600">
              Por favor, aguarde
            </p>
          </div>
        </div>
      `

      document.body.appendChild(overlay)
    }

    // Send AJAX request
    fetch(form.action, {
      method: 'POST',
      body: formData,
      headers: {
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        'Accept': 'application/json'
      }
    })
    .then(response => {
      // Check if response is ok and is JSON
      if (!response.ok) {
        return response.text().then(text => {
          try {
            return JSON.parse(text)
          } catch (e) {
            throw new Error(`Server error: ${response.status}`)
          }
        })
      }
      return response.json()
    })
    .then(data => {
      if (data.success && data.redirect_url) {
        // Redirect to 3D Secure or result page
        window.location.href = data.redirect_url
      } else {
        // Show error
        alert(data.error || 'Erro ao processar pagamento')

        // Remove any loading overlay
        this.removeAllOverlays()
      }
    })
    .catch(error => {
      console.error('Payment error:', error)
      alert(error.message || 'Erro ao processar pagamento. Por favor, tente novamente.')

      // Remove any loading overlay
      this.removeAllOverlays()
    })
  }

  removeAllOverlays() {
    document.body.style.overflow = ''
    document.getElementById('payment-processing-overlay')?.remove()
    document.getElementById('split-payment-processing-overlay')?.remove()
  }

  handleEndBuyButton(event) {
    // Remove extra input without value
    const sellerCodeFields = document.querySelectorAll('[name="seller_info[code]"]');
    const sellerCodeArray = Array.from(sellerCodeFields);
    const sellerCodeWithValueIdx = sellerCodeArray.findIndex(
      (element) => element.value !== ""
    );

    if (sellerCodeWithValueIdx != -1) {
      sellerCodeFields.forEach((element, idx) => {
        if(idx != sellerCodeWithValueIdx) {
          element.remove()
        }
      })
    }

    if( !this.paymentCardCheckTarget.classList.contains("hidden") ) {
      showPaymentProcessingOverlay()
    }
    else if ( !this.paymentPixCheckTarget.classList.contains("hidden") ) {
      showPaymentProcessingOverlay({
        title: "Gerando PIX",
        subtitle: "Aguarde um momento."
      })

      // Keep the original functionality for the form to work
      this.pixResultTarget.classList.remove("hidden")
      this.pixTextTarget.classList.add("hidden")
      this.captchaDivTarget.classList.add("hidden")
      this.paymentMethodsDivTarget.classList.add("hidden")
      this.endBuyButtonTarget.classList.add("hidden")
    }
  }
}